/**
 * Mobile-only slide-out drawer with full sidebar content.
 * Shown when hamburger is tapped. Uses Sheet from left.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Award, BarChart2, BookOpen, ChevronDown, ChevronRight, Folder as FolderIcon, Globe, Home, Layers, Plus, X } from "lucide-react";
import { Folder } from "@/types/linguadeck";
import { AppState } from "@/types/linguadeck";
import NewDeckDropdown from "./NewDeckDropdown";
import type { DeckInitialType } from "./DeckModal";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const NAV_ITEMS = [
  { id: "home" as const, path: "/", icon: Home, labelKey: "sidebar.home" },
  { id: "library" as const, path: "/library", icon: Layers, labelKey: "sidebar.mySets" },
  { id: "globalLibrary" as const, path: "/global-library", icon: Globe, labelKey: "sidebar.globalLibrary" },
  { id: "gamification" as const, path: "/gamification", icon: Award, labelKey: "header.achievements" },
  { id: "dashboard" as const, path: "/dashboard", icon: BarChart2, labelKey: "statistics.pageTitle" },
];

interface SidebarDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: Folder[];
  currentView: AppState["currentView"];
  onNavTo: (view: AppState["currentView"]) => void;
  onNewDeck: (type: DeckInitialType) => void;
  onCreateFolder?: () => void;
}

export default function SidebarDrawer({
  open,
  onOpenChange,
  folders,
  currentView,
  onNavTo,
  onNewDeck,
  onCreateFolder,
}: SidebarDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isFoldersOpen, setIsFoldersOpen] = useState(() => {
    const saved = localStorage.getItem("sidebarFoldersOpen");
    return saved !== "false";
  });

  React.useEffect(() => {
    localStorage.setItem("sidebarFoldersOpen", String(isFoldersOpen));
  }, [isFoldersOpen]);

  const handleNav = (view: AppState["currentView"], path: string, itemId?: string) => {
    onNavTo(itemId === "globalLibrary" ? "library" : view);
    navigate(path);
    onOpenChange(false);
  };

  const handleFolderClick = (folderId: string) => {
    navigate(`/folder/${folderId}`);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-[280px] max-w-[85vw] p-0 flex flex-col bg-[hsl(var(--bg2))] border-r border-[hsl(var(--border))] [&>button]:hidden"
      >
        {/* Header: Logo + Close */}
        <div
          className="flex-shrink-0 flex items-center justify-between px-4 py-4 border-b border-[hsl(var(--border))]"
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-8 h-8" style={{ color: "hsl(var(--primary))" }} />
            <span className="font-bold text-lg" style={{ color: "hsl(var(--foreground))" }}>
              StudyDo
            </span>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors"
            aria-label={t("common.close", "Close")}
          >
            <X size={22} strokeWidth={2} />
          </button>
        </div>

        {/* Nav items */}
        <div className="flex-shrink-0 px-3 py-3 border-b border-[hsl(var(--border))]">
          <div className="text-[10px] font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))] px-2 py-1 mb-2">
            {t("sidebar.menu")}
          </div>
          {NAV_ITEMS.map((item) => {
            const active =
              item.path === "/global-library"
                ? location.pathname.startsWith("/global-library")
                : location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path + "/"));
            const Icon = item.icon;
            return (
              <button
                key={item.labelKey}
                type="button"
                onClick={() => handleNav(item.id, item.path, item.id)}
                className="min-h-[44px] w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-left font-medium text-sm transition-colors"
                style={{
                  background: active ? "rgba(109,90,255,.15)" : "transparent",
                  borderLeft: active ? "3px solid hsl(var(--primary))" : "3px solid transparent",
                  color: "hsl(var(--foreground))",
                  fontWeight: active ? 600 : 400,
                }}
              >
                <Icon size={20} strokeWidth={2} className="shrink-0" />
                {t(item.labelKey)}
              </button>
            );
          })}
        </div>

        {/* Folders */}
        <div className="flex-1 overflow-y-auto min-h-0 px-3 py-3">
          <button
            type="button"
            onClick={() => setIsFoldersOpen((o) => !o)}
            className="min-h-[44px] w-full flex items-center justify-between px-2 py-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors"
          >
            <span className="text-[10px] font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
              {t("sidebar.folders")}
            </span>
            {isFoldersOpen ? (
              <ChevronDown size={14} className="text-[hsl(var(--muted-foreground))]" />
            ) : (
              <ChevronRight size={14} className="text-[hsl(var(--muted-foreground))]" />
            )}
          </button>
          {isFoldersOpen && (
            <div className="mt-2 space-y-0.5">
              {(folders ?? []).length === 0 ? (
                <p className="px-2 py-3 text-xs text-[hsl(var(--muted-foreground))]">
                  {t("folder.noFolders")}
                </p>
              ) : (
                (folders ?? []).map((folder) => (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => handleFolderClick(folder.id)}
                    className="min-h-[44px] w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors text-left"
                  >
                    <FolderIcon size={16} className="text-[hsl(var(--muted-foreground))] shrink-0" />
                    <span className="text-sm font-medium text-ellipsis overflow-hidden whitespace-nowrap">
                      {folder.title}
                    </span>
                  </button>
                ))
              )}
              {onCreateFolder && (
                <button
                  type="button"
                  onClick={() => {
                    onCreateFolder();
                    onOpenChange(false);
                  }}
                  className="min-h-[44px] w-full flex items-center gap-2 px-3 py-2.5 mt-2 rounded-xl bg-[hsl(var(--bg3))] hover:opacity-90 transition-opacity text-sm font-medium"
                  style={{ color: "hsl(var(--foreground))" }}
                >
                  <Plus size={18} className="shrink-0" />
                  {t("folder.newFolder")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Bottom: New deck */}
        {onCreateFolder && (
          <div className="flex-shrink-0 p-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--bg2))]">
            <NewDeckDropdown
              onSelect={(type) => {
                onNewDeck(type);
                onOpenChange(false);
              }}
              onCreateFolder={() => {
                onCreateFolder();
                onOpenChange(false);
              }}
              variant="full"
              openUpwards
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
